import { useEffect, useState } from "react";
import { Fragment } from "react";
import { api, API, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Download, FileText, FileSpreadsheet } from "lucide-react";
import ReportFilters, { rangeForPreset } from "@/components/common/ReportFilters";
import LedgerDrillDown from "@/components/common/LedgerDrillDown";

export default function ProfitLoss() {
    const { activeOrg } = useAuth();
    const [preset, setPreset] = useState("previous_year");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [basis, setBasis] = useState("accrual");
    const [view, setView] = useState("single"); // single | compare_previous | month | quarter
    const [data, setData] = useState(null);
    const [multi, setMulti] = useState(null);
    const [loading, setLoading] = useState(false);
    const [drill, setDrill] = useState({ open: false, accountId: null, accountName: "" });

    const openDrill = (row) => setDrill({ open: true, accountId: row.id, accountName: row.name });

    const run = async () => {
        const { from, to } = rangeForPreset(preset, customFrom, customTo);
        setLoading(true);
        try {
            if (view === "single") {
                const { data } = await api.get("/reports/profit-loss", { params: { date_from: from, date_to: to, basis } });
                setData(data);
                setMulti(null);
            } else {
                const { data } = await api.get("/reports/profit-loss/multi-period", {
                    params: { date_from: from, date_to: to, basis, granularity: view },
                });
                setMulti(data);
                setData(null);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { run(); /* eslint-disable-next-line */ }, [view]);

    const exportFile = async (format) => {
        const { from, to } = rangeForPreset(preset, customFrom, customTo);
        const win = format === "pdf" ? window.open("", "_blank") : null;
        const token = localStorage.getItem("vyastra_token");
        const url = `${API}/reports/profit-loss/export?date_from=${from}&date_to=${to}&basis=${basis}&format=${format}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        if (format === "pdf") {
            if (win && !win.closed) win.location.href = blobUrl;
            else { const a = document.createElement("a"); a.href = blobUrl; a.target = "_blank"; document.body.appendChild(a); a.click(); a.remove(); }
        } else {
            const ext = format === "xlsx" ? "xlsx" : "csv";
            const a = document.createElement("a");
            a.href = blobUrl; a.download = `PL_${from}_${to}.${ext}`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(blobUrl);
        }
    };

    const period = data?.period || {};
    const orgName = activeOrg?.legal_name || activeOrg?.name || "";

    return (
        <>
            <PageHeader kicker="REPORTS" title="Profit & Loss" subtitle="Account-level revenue, COGS, expenses and net profit. Toggle Cash vs Accrual to see the basis you need." />
            <PageBody>
                <ReportFilters
                    preset={preset} onPresetChange={(v) => { setPreset(v); }}
                    customFrom={customFrom} customTo={customTo}
                    onCustomFromChange={setCustomFrom} onCustomToChange={setCustomTo}
                    basis={basis} onBasisChange={setBasis}
                    onRun={run}
                    rightSlot={<>
                        <div className="flex items-center gap-1.5">
                            <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">View</Label>
                            <Select value={view} onValueChange={setView}>
                                <SelectTrigger className="h-9 w-[180px]" data-testid="pl-view"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="single">Single period</SelectItem>
                                    <SelectItem value="compare_previous">vs Previous Year</SelectItem>
                                    <SelectItem value="month">Month-by-month</SelectItem>
                                    <SelectItem value="quarter">Quarter-by-quarter</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="pl-export-pdf"><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
                        <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} data-testid="pl-export-xlsx"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
                        <Button variant="outline" size="sm" onClick={() => exportFile("csv")} data-testid="pl-export-csv"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
                    </>}
                />

                {loading && <div className="text-center text-muted-foreground py-12">Crunching numbers…</div>}
                {multi && !loading && (
                    <MultiPeriodPL data={multi} orgName={orgName} baseCurrency={activeOrg?.base_currency || "INR"} onRowClick={openDrill} />
                )}
                {data && !loading && (
                    <div className="bg-card border border-border rounded-md max-w-[920px] mx-auto" data-testid="pl-report">
                        {/* Statement header */}
                        <div className="text-center py-8 border-b border-border">
                            <div className="font-display text-[22px] font-light tracking-tight">{orgName}</div>
                            <div className="font-mono text-[13px] text-muted-foreground mt-0.5">Profit and Loss</div>
                            <div className="text-[12px] text-muted-foreground mt-0.5">From {period.from} To {period.to}</div>
                            <div className="inline-flex mt-3 px-2.5 py-0.5 rounded-full bg-muted text-[10px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
                                Basis: {data.basis === "cash" ? "Cash" : "Accrual"}
                            </div>
                        </div>
                        {/* Two-column ledger style */}
                        <table className="w-full text-[13px] [&_td]:py-1.5 [&_td]:px-6 [&_th]:px-6 [&_th]:py-2.5">
                            <thead className="bg-muted/40 border-b border-border">
                                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                                    <th className="w-1/2">Account</th>
                                    <th className="text-right">Total ({activeOrg?.base_currency || "INR"})</th>
                                </tr>
                            </thead>
                            <tbody>
                                <SectionGroup title="Operating Income" groups={data.income_grouped} total={data.totals.income} totalLabel="Total for Operating Income" onRowClick={openDrill} />
                                {data.cogs_grouped.length > 0 && (
                                    <SectionGroup title="Cost of Goods Sold" groups={data.cogs_grouped} total={data.totals.cogs} totalLabel="Total for Cost of Goods Sold" onRowClick={openDrill} />
                                )}
                                {data.cogs_grouped.length > 0 && (
                                    <SubtotalRow label="Gross Profit" value={data.totals.gross_profit} />
                                )}
                                {data.expense_grouped.length > 0 && (
                                    <SectionGroup title="Operating Expense" groups={data.expense_grouped} total={data.totals.expense} totalLabel="Total for Operating Expense" onRowClick={openDrill} />
                                )}
                                <SubtotalRow label="Operating Profit" value={data.totals.operating_profit} />
                                {data.other_income_grouped.length > 0 && (
                                    <SectionGroup title="Non Operating Income" groups={data.other_income_grouped} total={data.totals.other_income} totalLabel="Total for Non Operating Income" onRowClick={openDrill} />
                                )}
                                {data.other_expense_grouped.length > 0 && (
                                    <SectionGroup title="Non Operating Expense" groups={data.other_expense_grouped} total={data.totals.other_expense} totalLabel="Total for Non Operating Expense" onRowClick={openDrill} />
                                )}
                                <tr className="border-t-2 border-foreground">
                                    <td className="py-3 font-semibold text-[14px]">Net Profit/Loss</td>
                                    <td className={`py-3 text-right font-mono tabular-nums font-semibold text-[14px] ${data.totals.net_profit >= 0 ? "text-positive" : "text-destructive"}`}>
                                        {fmtMoney(data.totals.net_profit, activeOrg?.base_currency)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div className="text-[10px] text-center text-muted-foreground py-4 border-t border-border">
                            ** Amount is displayed in your base currency {activeOrg?.base_currency || "INR"}
                        </div>
                    </div>
                )}
            </PageBody>
            <LedgerDrillDown
                open={drill.open}
                onOpenChange={(o) => setDrill((d) => ({ ...d, open: o }))}
                accountId={drill.accountId}
                accountName={drill.accountName}
                dateFrom={data?.period?.from}
                dateTo={data?.period?.to}
            />
        </>
    );
}

function SectionGroup({ title, groups, total, totalLabel, onRowClick }) {
    return (
        <>
            <tr className="bg-muted/30">
                <td colSpan={2} className="font-semibold text-[12px] uppercase tracking-[0.1em] py-2.5">{title}</td>
            </tr>
            {groups.map((g) => (
                <Fragment key={`g-${g.group}`}>
                    {g.group !== "—" && (
                        <tr>
                            <td className="pl-10 text-muted-foreground text-[11.5px] uppercase tracking-[0.08em] pt-3">{g.group}</td>
                            <td></td>
                        </tr>
                    )}
                    {g.rows.map((r) => (
                        <tr
                            key={r.id}
                            className="hover:bg-muted/40 cursor-pointer group"
                            onClick={() => onRowClick && onRowClick(r)}
                            data-testid={`pl-row-${r.id}`}
                            title="Click to view ledger transactions"
                        >
                            <td className={`${g.group !== "—" ? "pl-14" : "pl-10"}`}>
                                <span className="font-mono text-[11px] text-muted-foreground mr-2 tabular-nums">{r.code}</span>
                                <span className="group-hover:text-gold group-hover:underline underline-offset-2 decoration-gold/40">{r.name}</span>
                            </td>
                            <td className="text-right font-mono tabular-nums group-hover:text-gold">{fmtMoney(r.amount)}</td>
                        </tr>
                    ))}
                </Fragment>
            ))}
            <tr className="border-t border-border">
                <td className="font-medium pl-10 py-2">{totalLabel}</td>
                <td className="text-right font-mono tabular-nums font-medium">{fmtMoney(total)}</td>
            </tr>
        </>
    );
}

function SubtotalRow({ label, value }) {
    return (
        <tr className="bg-[#fdf6ec] border-y border-border">
            <td className="font-semibold py-3">{label}</td>
            <td className={`text-right font-mono tabular-nums font-semibold ${value >= 0 ? "text-positive" : "text-destructive"}`}>{fmtMoney(value)}</td>
        </tr>
    );
}


const SECTION_LABELS = {
    income: "Operating Income",
    cogs: "Cost of Goods Sold",
    expense: "Operating Expense",
    other_income: "Non-Operating Income",
    other_expense: "Non-Operating Expense",
};

function MultiPeriodPL({ data, orgName, baseCurrency, onRowClick }) {
    const cols = data.columns || [];
    const rowsByType = {};
    (data.rows || []).forEach((r) => {
        rowsByType[r.type] = rowsByType[r.type] || [];
        rowsByType[r.type].push(r);
    });
    const isCompare = data.granularity === "compare_previous" && cols.length === 2;
    return (
        <div className="bg-card border border-border rounded-md max-w-full overflow-x-auto" data-testid="pl-multi-report">
            <div className="text-center py-6 border-b border-border sticky left-0 bg-card">
                <div className="font-display text-[20px] font-light tracking-tight">{orgName}</div>
                <div className="font-mono text-[12px] text-muted-foreground mt-0.5">Profit and Loss · {data.granularity === "month" ? "Month-by-month" : data.granularity === "quarter" ? "Quarter-by-quarter" : data.granularity === "compare_previous" ? "Year-over-year" : "Multi-period"}</div>
                <div className="inline-flex mt-2 px-2 py-0.5 rounded-full bg-muted text-[10px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">Basis: {data.basis === "cash" ? "Cash" : "Accrual"}</div>
            </div>
            <table className="w-full text-[12.5px]">
                <thead className="bg-muted/40 border-b border-border">
                    <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                        <th className="px-4 py-2.5 sticky left-0 bg-muted/40 min-w-[260px]">Account</th>
                        {cols.map((c, i) => (
                            <th key={i} className="px-4 py-2.5 text-right whitespace-nowrap" data-testid={`pl-col-${i}`}>{c.label}<div className="font-mono text-[9px] text-muted-foreground/70 normal-case mt-0.5">{c.from} → {c.to}</div></th>
                        ))}
                        {isCompare && <th className="px-4 py-2.5 text-right">Variance</th>}
                        {isCompare && <th className="px-4 py-2.5 text-right">Var %</th>}
                        {!isCompare && <th className="px-4 py-2.5 text-right">Total</th>}
                    </tr>
                </thead>
                <tbody>
                    {Object.keys(SECTION_LABELS).map((t) => {
                        const rs = rowsByType[t] || [];
                        if (rs.length === 0) return null;
                        return (
                            <Fragment key={t}>
                                <tr className="bg-muted/30">
                                    <td colSpan={cols.length + 2 + (isCompare ? 1 : 0)} className="px-4 py-2 font-semibold text-[11.5px] uppercase tracking-[0.1em] sticky left-0 bg-muted/30">{SECTION_LABELS[t]}</td>
                                </tr>
                                {rs.map((r) => {
                                    const last = r.amounts[r.amounts.length - 1] || 0;
                                    const prev = r.amounts[0] || 0;
                                    const variance = last - prev;
                                    const varPct = Math.abs(prev) > 0.01 ? (variance / Math.abs(prev) * 100) : null;
                                    return (
                                        <tr key={r.id} className="hover:bg-muted/40 cursor-pointer group" onClick={() => onRowClick && onRowClick({ id: r.id, name: r.name })} data-testid={`pl-multi-row-${r.id}`}>
                                            <td className="px-4 py-1.5 pl-8 sticky left-0 bg-card group-hover:bg-muted/40">
                                                <span className="font-mono text-[11px] text-muted-foreground mr-2 tabular-nums">{r.code}</span>
                                                <span className="group-hover:text-gold group-hover:underline underline-offset-2 decoration-gold/40">{r.name}</span>
                                            </td>
                                            {r.amounts.map((a, i) => (
                                                <td key={i} className="px-4 py-1.5 text-right font-mono tabular-nums whitespace-nowrap">{fmtMoney(a)}</td>
                                            ))}
                                            {isCompare && (
                                                <td className={`px-4 py-1.5 text-right font-mono tabular-nums ${variance >= 0 ? "text-positive" : "text-destructive"}`}>{fmtMoney(variance)}</td>
                                            )}
                                            {isCompare && (
                                                <td className={`px-4 py-1.5 text-right font-mono tabular-nums ${variance >= 0 ? "text-positive" : "text-destructive"}`}>{varPct == null ? "—" : `${varPct.toFixed(1)}%`}</td>
                                            )}
                                            {!isCompare && (
                                                <td className="px-4 py-1.5 text-right font-mono tabular-nums font-medium">{fmtMoney(r.total)}</td>
                                            )}
                                        </tr>
                                    );
                                })}
                                <tr className="border-t border-border bg-muted/15">
                                    <td className="px-4 py-2 font-medium pl-8 sticky left-0 bg-card">Total {SECTION_LABELS[t]}</td>
                                    {(data.section_totals[t] || []).map((v, i) => (
                                        <td key={i} className="px-4 py-2 text-right font-mono tabular-nums font-medium whitespace-nowrap">{fmtMoney(v)}</td>
                                    ))}
                                    {isCompare && <td className="px-4 py-2 text-right font-mono tabular-nums">—</td>}
                                    {isCompare && <td className="px-4 py-2 text-right font-mono tabular-nums">—</td>}
                                    {!isCompare && <td className="px-4 py-2 text-right font-mono tabular-nums font-medium">{fmtMoney((data.section_totals[t] || []).reduce((s, x) => s + x, 0))}</td>}
                                </tr>
                            </Fragment>
                        );
                    })}
                    <tr className="border-t-2 border-foreground bg-[#fdf6ec]">
                        <td className="px-4 py-3 font-semibold sticky left-0 bg-[#fdf6ec]">Net Profit / (Loss)</td>
                        {(data.totals.net_profit || []).map((v, i) => (
                            <td key={i} className={`px-4 py-3 text-right font-mono tabular-nums font-semibold whitespace-nowrap ${v >= 0 ? "text-positive" : "text-destructive"}`}>{fmtMoney(v)}</td>
                        ))}
                        {isCompare && (() => {
                            const a = data.totals.net_profit?.[0] || 0;
                            const b = data.totals.net_profit?.[1] || 0;
                            const v = b - a;
                            const p = Math.abs(a) > 0.01 ? (v / Math.abs(a) * 100) : null;
                            return [
                                <td key="v" className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${v >= 0 ? "text-positive" : "text-destructive"}`}>{fmtMoney(v)}</td>,
                                <td key="p" className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${v >= 0 ? "text-positive" : "text-destructive"}`}>{p == null ? "—" : `${p.toFixed(1)}%`}</td>,
                            ];
                        })()}
                        {!isCompare && (
                            <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold">{fmtMoney((data.totals.net_profit || []).reduce((s, x) => s + x, 0))}</td>
                        )}
                    </tr>
                </tbody>
            </table>
            <div className="text-[10px] text-center text-muted-foreground py-3 border-t border-border">** All amounts in {baseCurrency}</div>
        </div>
    );
}
